<template>
  <div v-hotkey="editShortcuts" class="canvas-page">
    <CanvasTopBar
      :database="database"
      :object-id="rowId"
      :organization-id="organizationId"
      :hierarchy="hierarchy"
      :is-edit-mode="isEditMode"
      :is-loading="isLoading"
      :error="error"
      :show-datepicker="topBarDatepickerEnabled"
      :selected-date="selectedDate"
      @toggle-edit="toggleEditMode"
      @add-day="addDay"
      @go-today="goToday"
      @select-date="selectDate"
    />

    <!-- height-screen: the canvas owns exactly the viewport (no page
         scroll, widgets scroll internally); otherwise it flows and the
         whole screen scrolls (`CanvasTabHeight`). -->
    <div
      class="canvas-screen"
      :class="{ 'height-screen': heightMode === 'screen' }"
    >
      <!-- Header: always shown in edit mode, with the canvas type above
           the title (like an object page's database chip). In view mode
           only the title, and only when the Canvas section's Title toggle
           is on — the type chip never shows there. -->
      <div v-if="isEditMode || titleEnabled" class="canvas-header">
        <div class="canvas-heading">
          <BaseType
            v-if="isEditMode && database"
            state="box"
            :image="getTypeIcon(profile, database)"
            :color="getTypeColor(database)"
            :title="database.data_name"
            @click="openDatabase"
          />
          <h1 class="canvas-title">
            <RInput
              v-if="isEditMode"
              :value="draftTitle"
              :debounce="0"
              placeholder="Untitled"
              class="title-input"
              @update:value="draftTitle = $event"
            />
            <span v-else class="name">{{ canvasTitle }}</span>
          </h1>
        </div>
      </div>

      <div v-if="error" class="canvas-error">
        <p>{{ error }}</p>
      </div>

      <div v-else-if="isLoading" class="canvas-loading">
        <p>Loading canvas...</p>
      </div>

      <div v-else class="canvas-content">
        <div
          v-if="canvasBody"
          class="canvas-body"
          :class="{ editing: isEditMode }"
        >
          <div class="canvas-main">
            <div v-if="canvasBody.tabs.length > 1 || isEditMode" class="tabs">
              <BaseButton
                v-for="tab in canvasBody.tabs"
                :key="tab.id"
                class="canvas-tab"
                :class="{ selected: tab.id === activeTabId }"
                kind="auxiliary"
                @click="selectTab(tab.id)"
              >
                <template #title>
                  <template v-if="renamingTabId === tab.id">
                    <input
                      :ref="setTabRenameInputEl"
                      v-model="tabNameDraft"
                      class="tab-rename-input"
                      type="text"
                      @click.stop
                      @keydown.enter.prevent="commitTabRename"
                      @keydown.esc.prevent="cancelTabRename"
                      @blur="commitTabRename"
                    />
                  </template>
                  <span
                    v-else
                    class="tab-name"
                    :class="{ editable: isEditMode && tab.id === activeTabId }"
                    @click.stop="
                      isEditMode && tab.id === activeTabId
                        ? startRenamingActiveTab()
                        : selectTab(tab.id)
                    "
                  >
                    {{ tab.name }}
                  </span>
                </template>
              </BaseButton>
              <BaseButton
                v-if="isEditMode"
                kind="auxiliary"
                class="tab-add"
                tooltip="New tab"
                tooltip-display="always"
                @click="addTab"
              >
                <template #icon>
                  <AddIcon />
                </template>
              </BaseButton>
              <div class="tabs-spacer" />
            </div>

            <div class="workspace">
              <CanvasSlotNode
                class="root-slot"
                :content="currentRoot"
                :path="[]"
                :height-mode="heightMode"
                :is-edit-mode="isEditMode"
                :selected-path="selectedPath"
                :workspace-id="workspaceId"
                :query-params="queryParams"
                :view-parameters="viewParameters"
                :storage-prefix="storagePrefix"
                :parent-id="rowId"
                :widget-date="widgetDate"
                :calendars-map="emptyCalendarsMap"
                :view-config-host="viewConfigHostEl"
                :drop-widget="dropWidget"
                :remove-widget="removeWidget"
                :select-slot="selectSlot"
                :update-query="setWidgetQuery"
                :update-display="setWidgetDisplay"
                :resize-layout-tracks="resizeLayoutTracks"
              />
            </div>
          </div>

          <div v-if="isEditMode" class="canvas-right-pane">
            <!-- Canvas-wide options: always shown, whatever the selection. -->
            <div class="pane-section canvas-options">
              <div class="pane-title">Canvas</div>
              <div class="toggle-option">
                <label>Title</label>
                <BaseToggleButton
                  :checked="titleEnabled"
                  @change="toggleOption('title')"
                />
              </div>
              <div class="toggle-option">
                <label>Date picker</label>
                <BaseToggleButton
                  :checked="topBarDatepickerEnabled"
                  @change="toggleOption('datepicker')"
                />
              </div>
              <!-- Deletes the active tab and its whole slot tree; a canvas
                   always keeps at least one tab. -->
              <div class="clear-row">
                <BaseButton
                  kind="auxiliary"
                  tint="red"
                  text="Delete"
                  class="clear-button"
                  :state="canDeleteActiveTab ? 'default' : 'disabled'"
                  @click="deleteActiveTab"
                />
              </div>
            </div>

            <!-- Inspector: the pane is contextual — a selected widget swaps
                 the palette for that widget's configuration. -->
            <div v-if="selectedWidget" class="pane-section inspector">
              <div class="inspector-header">
                <div class="pane-title">{{ selectedWidgetTitle }}</div>
                <button
                  class="inspector-close"
                  type="button"
                  title="Done"
                  @click="selectSlot(null)"
                >
                  <CloseIcon />
                </button>
              </div>
              <!-- The selected View teleports its configuration here (see
                   `View.configHost`). -->
              <div
                v-if="selectedWidget.widget.kind === 'view'"
                ref="viewConfigHostEl"
                class="view-config-host"
              />
              <div
                v-else-if="selectedWidget.widget.kind === 'layout'"
                class="layout-inspector"
              >
                <!-- Reshaping preserves content: widgets in removed cells
                     relocate to free cells, and a step that would lose a
                     widget is disabled (see `resizeCanvasLayout`). -->
                <div class="size-row">
                  <label>Rows</label>
                  <div class="stepper">
                    <button
                      type="button"
                      :disabled="!canResizeSelectedLayout(-1, 0)"
                      @click="resizeSelectedLayout(-1, 0)"
                    >
                      −
                    </button>
                    <span class="count">{{ selectedWidget.widget.rows }}</span>
                    <button
                      type="button"
                      :disabled="!canResizeSelectedLayout(1, 0)"
                      @click="resizeSelectedLayout(1, 0)"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div class="size-row">
                  <label>Columns</label>
                  <div class="stepper">
                    <button
                      type="button"
                      :disabled="!canResizeSelectedLayout(0, -1)"
                      @click="resizeSelectedLayout(0, -1)"
                    >
                      −
                    </button>
                    <span class="count">{{
                      selectedWidget.widget.columns
                    }}</span>
                    <button
                      type="button"
                      :disabled="!canResizeSelectedLayout(0, 1)"
                      @click="resizeSelectedLayout(0, 1)"
                    >
                      +
                    </button>
                  </div>
                </div>
                <p class="inspector-hint">
                  Drop widgets into the grid's slots. Widgets in removed cells
                  move to free cells; shrinking is disabled when they would not
                  fit.
                </p>
              </div>
              <div
                v-else-if="selectedWidget.widget.kind === 'tasks'"
                class="tasks-inspector"
              >
                <!-- One toggle per section. Retractable titled sections when
                     several are enabled; a single one renders as a bare
                     list without a header. -->
                <div
                  v-for="option in tasksSectionOptions"
                  :key="option.id"
                  class="toggle-option"
                >
                  <label>{{ option.label }}</label>
                  <BaseToggleButton
                    :checked="
                      selectedWidget.widget.sections.includes(option.id)
                    "
                    @change="toggleTasksSection(selectedWidget.id, option.id)"
                  />
                </div>
                <p class="inspector-hint">
                  Task lists for the selected day. With a single section
                  enabled, its header is hidden.
                </p>
              </div>
              <div
                v-else-if="selectedWidget.widget.kind === 'calendar'"
                class="calendar-inspector"
              >
                <div class="select-row">
                  <label>Default layout</label>
                  <BaseInputSelect
                    class="layout-select"
                    :value="selectedWidget.widget.layout ?? 'week'"
                    :options="calendarLayoutOptions"
                    select-only
                    @selected="
                      setCalendarLayout(selectedWidget.id, $event.value)
                    "
                  />
                </div>
                <div
                  v-for="option in calendarToggleOptions"
                  :key="option.id"
                  class="toggle-option"
                >
                  <label>{{ option.label }}</label>
                  <BaseToggleButton
                    :checked="selectedWidget.widget[option.id] !== false"
                    @change="toggleCalendarOption(selectedWidget.id, option.id)"
                  />
                </div>
                <p class="inspector-hint">
                  A display-only calendar. Double-click an event to open it.
                </p>
              </div>
              <p v-else class="inspector-hint">
                Shows the timeline of the selected day. Nothing to configure.
              </p>
              <!-- Clears the slot (removes its widget); the slot stays
                   selected and empty, ready to be refilled. Same shape as
                   the DatePicker's Clear. -->
              <div class="clear-row">
                <BaseButton
                  kind="auxiliary"
                  tint="red"
                  text="Clear"
                  class="clear-button"
                  @click="removeWidget(selectedWidget.id)"
                />
              </div>
            </div>

            <!-- Palette: how slots get filled. With an empty slot selected
                 it doubles as that slot's inspector: its header gains the
                 Done button and the slot's track deletion sits at the
                 bottom (see `emptySlotActions`). -->
            <div v-else class="pane-section">
              <div v-if="isEmptySlotSelected" class="inspector-header">
                <div class="pane-title">Empty slot</div>
                <button
                  class="inspector-close"
                  type="button"
                  title="Done"
                  @click="selectSlot(null)"
                >
                  <CloseIcon />
                </button>
              </div>
              <div v-else class="pane-title">Widgets</div>

              <div class="pane-subtitle">Layouts</div>
              <div class="widget-palette layout-palette">
                <div
                  v-for="layout in layoutPalette"
                  :key="layout.label"
                  class="palette-item layout-item"
                  draggable="true"
                  @dragstart="
                    onPaletteDragStart($event, {
                      kind: 'layout',
                      rows: layout.rows,
                      columns: layout.columns,
                    })
                  "
                >
                  {{ layout.label }}
                </div>
              </div>

              <div class="pane-subtitle">Content</div>
              <div class="widget-palette">
                <div
                  class="palette-item"
                  draggable="true"
                  @dragstart="onPaletteDragStart($event, { kind: 'view' })"
                >
                  View
                </div>
                <div
                  class="palette-item"
                  draggable="true"
                  @dragstart="onPaletteDragStart($event, { kind: 'timeline' })"
                >
                  Timeline
                </div>
                <div
                  class="palette-item"
                  draggable="true"
                  @dragstart="onPaletteDragStart($event, { kind: 'tasks' })"
                >
                  Tasks
                </div>
                <div
                  class="palette-item"
                  draggable="true"
                  @dragstart="onPaletteDragStart($event, { kind: 'calendar' })"
                >
                  Calendar
                </div>
              </div>

              <!-- Removing the selected empty slot drops its row/column;
                   widgets sharing the track move to free cells. -->
              <div
                v-for="action in emptySlotActions"
                :key="action.label"
                class="clear-row"
              >
                <BaseButton
                  kind="auxiliary"
                  tint="red"
                  :text="action.label"
                  :state="action.enabled ? 'default' : 'disabled'"
                  class="clear-button"
                  @click="deleteEmptySlotTrack(action)"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-else class="canvas-raw">
          <GraphJson :data="bodyValue" name="Body" state="full" />
        </div>
      </div>
    </div>
    <div v-if="isEditMode" class="save-floating">
      <BaseButton
        v-if="isDirty"
        kind="main"
        text="Save"
        @click="saveAndExitEditMode"
      >
        <template #icon>
          <CompleteIcon />
        </template>
      </BaseButton>
      <BaseButton kind="auxiliary" text="Cancel" @click="cancelEditMode" />
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  provide,
  ref,
  shallowRef,
  toRefs,
  watch,
  type PropType,
} from "vue";

import type {
  GraphResult,
  GraphVariableDictionary,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import BaseToggleButton from "@/components/base/BaseToggleButton.vue";
import BaseType from "@/components/base/BaseType.vue";
import { getTypeColor, getTypeIcon } from "@/components/databases";
import {
  setGraphPropertyRaw,
  setJsonProperty,
} from "@/components/graph/component";
import GraphJson from "@/components/graph/GraphJson.vue";
import {
  asGraphRecordResult,
  asJson,
  asString,
  isGraphResult,
  isPlainRecord,
} from "@/components/graph/result";
import { useGraphObject } from "@/components/graph/useGraphObject";
import AddIcon from "@/components/icons/AddIcon.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import CompleteIcon from "@/components/icons/CompleteIcon.vue";
import * as inject from "@/components/inject";
import RInput from "@/components/RInput.vue";
import { assignShortcuts, type Shortcuts } from "@/components/shortcuts";
import type { Query } from "@/components/views/View.vue";
import { useReleaser } from "@/ref";
import { navigateToDatabase } from "@/utils/navigate";

import {
  CANVAS_CALENDAR_LAYOUTS,
  CANVAS_TASKS_SECTIONS,
  getSlotAt,
  isCanvasJsonValue,
  parseCanvasBodyV1,
  removeCanvasLayoutTrack,
  removeWidgetById,
  resizeCanvasLayout,
  setSlotAt,
  updateWidgetById,
  type CanvasBodyV1,
  type CanvasCalendarWidget,
  type CanvasLayoutWidget,
  type CanvasSlot,
  type CanvasSlotPath,
  type CanvasTab,
  type CanvasTabHeight,
  type CanvasTasksSection,
  type CanvasWidget,
} from "./body";
import CanvasSlotNode from "./CanvasSlotNode.vue";
import CanvasTopBar from "./CanvasTopBar.vue";
import { writeWidgetDropPayload, type CanvasWidgetDropPayload } from "./dnd";
import { getCanvasesGraphDatabase } from "./useCanvases";

const makeId = (): string => {
  try {
    return crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
  } catch {
    return `${Date.now()}-${Math.random()}`;
  }
};

const defaultCanvasBodyV1 = (): CanvasBodyV1 => ({
  schemaVersion: 1,
  tabs: [{ id: makeId(), name: "Tab 1", root: null }],
  inputParams: [],
  outputParams: [],
});

// `options` is conditional, not `options: body.options`: an explicit
// `undefined` property is not a GraphResult, so it would make
// `setJsonProperty` silently persist the whole body as null.
const withTabs = (body: CanvasBodyV1, tabs: CanvasTab[]): CanvasBodyV1 => ({
  schemaVersion: 1,
  tabs,
  inputParams: body.inputParams,
  outputParams: body.outputParams,
  ...(body.options === undefined ? {} : { options: body.options }),
});

/** Editor-side cap on layout grid dimensions (the model has no limit). */
const MAX_LAYOUT_TRACKS = 6;

/** Clear the selected slot's widget while editing (`editShortcuts`). */
const clearSelectedSlotKeys = ref(["backspace", "delete"]);

/** A track deletion offered when an empty slot is selected. */
type EmptySlotAction = {
  label: string;
  /** The parent layout widget owning the deleted track. */
  parentId: string;
  axis: "rows" | "columns";
  index: number;
  /** False when relocating the track's widgets would not fit. */
  enabled: boolean;
};

/** "1×2" = 1 row, 2 columns. No 1×1: a single slot adds nothing over the
 * slot it would occupy. */
const LAYOUT_PALETTE = [
  { label: "1×2", rows: 1, columns: 2 },
  { label: "2×1", rows: 2, columns: 1 },
  { label: "2×2", rows: 2, columns: 2 },
] as const;

const widgetFromPayload = (payload: CanvasWidgetDropPayload): CanvasWidget => {
  switch (payload.kind) {
    case "layout":
      return {
        id: makeId(),
        widget: {
          kind: "layout",
          rows: payload.rows,
          columns: payload.columns,
          slots: Array.from(
            { length: payload.rows * payload.columns },
            (): CanvasSlot => null
          ),
        },
      };
    case "view":
      // No `display` yet: the view renders its defaults and emits its first
      // display document on the first configuration change.
      return {
        id: makeId(),
        widget: { kind: "view", query: "" },
      };
    case "timeline":
      return { id: makeId(), widget: { kind: "timeline" } };
    case "tasks":
      // Start with the day's tasks only: a single section renders as a bare
      // list; the inspector's toggles enable more.
      return { id: makeId(), widget: { kind: "tasks", sections: ["day"] } };
    case "calendar":
      // A bare body means every default: all toggles shown, "week" layout.
      return { id: makeId(), widget: { kind: "calendar" } };
  }
};

/** Inspector toggle per task section, in canonical order. */
const TASKS_SECTION_OPTIONS: { id: CanvasTasksSection; label: string }[] = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "overdue", label: "Overdue" },
  { id: "upcoming", label: "Upcoming" },
  { id: "unplanned", label: "Unplanned" },
  { id: "completed", label: "Completed" },
  { id: "discarded", label: "Discarded" },
];

/** The Calendar widget's show/hide options (absent fields mean shown). */
type CanvasCalendarToggle = keyof Pick<
  CanvasCalendarWidget,
  "timezones" | "zoom" | "layouts" | "footer"
>;

/** Inspector toggle per calendar option. */
const CALENDAR_TOGGLE_OPTIONS: { id: CanvasCalendarToggle; label: string }[] = [
  { id: "timezones", label: "Timezones" },
  { id: "zoom", label: "Zoom buttons" },
  { id: "layouts", label: "Layout buttons" },
  { id: "footer", label: "Footer" },
];

/** Selector options for the Calendar widget's default layout. */
const CALENDAR_LAYOUT_OPTIONS: SelectOption[] = CANVAS_CALENDAR_LAYOUTS.map(
  (layout) => ({
    value: layout,
    label:
      layout === "week"
        ? "Week"
        : layout === "month"
          ? "Month"
          : layout.replace("-day", " day"),
    aliases: [],
  })
);

function coerceToGraphRecordResult(
  value: unknown
): Record<string, GraphResult> | undefined {
  if (value === null || value === undefined) return undefined;
  if (isPlainRecord(value)) {
    return isGraphResult(value) ? value : undefined;
  }
  if (value instanceof Map) {
    const out: Record<string, GraphResult> = {};
    for (const [key, entry] of value.entries()) {
      if (typeof key !== "string") continue;
      if (!isGraphResult(entry)) return undefined;
      out[key] = entry;
    }
    return Object.keys(out).length > 0 ? out : undefined;
  }
  return undefined;
}

export default defineComponent({
  name: "Canvas",
  components: {
    AddIcon,
    BaseButton,
    BaseInputSelect,
    BaseToggleButton,
    BaseType,
    CanvasSlotNode,
    CanvasTopBar,
    CloseIcon,
    CompleteIcon,
    GraphJson,
    RInput,
  },
  props: {
    organizationId: { type: String, required: true },
    workspaceId: {
      type: String as PropType<WorkspaceId>,
      required: true,
    },
    rowId: { type: String, required: true },
    /** Enter edit mode as soon as the canvas is loaded (e.g. a canvas
     * freshly created from the Studio screen, opened via `?edit=1`). */
    startInEditMode: { type: Boolean, default: false },
  },
  async setup(props) {
    const { workspaceId, rowId } = toRefs(props);
    const controller = inject.controller();
    const logger = inject.logger("Canvas");
    const profile = inject.profile();
    const router = inject.router();
    const workspaceDatabases = inject.workspaceDatabases();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();

    logger.info("Canvas setup", {
      workspaceId: workspaceId.value,
      rowId: rowId.value,
    });

    // The workspace's canvases database, resolved reactively from the
    // database catalog (it may not be loaded yet on a cold deep link).
    const tableId = computed(
      () =>
        getCanvasesGraphDatabase(workspaceDatabases.value?.[workspaceId.value])
          ?.id ?? ""
    );

    const objectError = ref<string | null>(null);
    const error = computed<string | null>(() => {
      if (workspaceDatabases.value !== undefined && !tableId.value) {
        return "Canvas type is not initialized for this workspace (missing canvases database).";
      }
      return objectError.value;
    });

    const parent = computed(() =>
      rowId.value
        ? ({ kind: "object" as const, id: rowId.value } as const)
        : null
    );
    provide("parent", parent);

    const mode = ref<"autosave">("autosave");

    const releaser = useReleaser();
    // useGraphObject must be called before any other await (uses inject
    // internally).
    const session = await releaser.add(
      useGraphObject(
        controller,
        workspaceDatabases,
        computed(() => workspaceId.value),
        tableId,
        computed(() => rowId.value),
        mode
      )
    );

    const objectData = computed<GraphResult | undefined>(
      () => session.objectData.value
    );

    const isLoading = ref(true);
    releaser.defer(
      watch(
        session.objectData,
        () => {
          // In this codebase, "still loading" is usually represented as
          // `undefined`. The row query may resolve to `null` if not found;
          // treat that as "done".
          if (session.objectData.value === undefined) return;
          isLoading.value = false;
        },
        { immediate: true }
      )
    );

    const record = computed(() => {
      const raw = objectData.value;
      const asRecord = raw ? asGraphRecordResult(raw) : undefined;
      return asRecord ?? coerceToGraphRecordResult(raw);
    });
    const title = computed(() => {
      const v = record.value?.Title;
      return v === undefined ? undefined : asString(v);
    });
    const bodyValue = computed<GraphResult | undefined>(
      () => record.value?.Body
    );
    const bodyJson = computed(() => asJson(record.value?.Body));

    const canvasTitle = computed(() => title.value ?? "Untitled");

    /** Open the Canvases database, like an object page's type chip. */
    const openDatabase = async () => {
      const db = session.db.value;
      if (!db) return;
      await navigateToDatabase(router, db.workspace, db.id);
    };

    const draftTitle = ref(canvasTitle.value);
    const savedTitle = ref(canvasTitle.value);
    const isEditMode = ref(false);

    releaser.defer(
      watch(canvasTitle, (v) => {
        if (!isEditMode.value) {
          draftTitle.value = v;
          savedTitle.value = v;
        }
      })
    );

    const viewParameters = computed(() => {
      const editing = isEditMode.value;
      return {
        header: editing,
        filter: editing,
        sort: editing,
        open: !editing,
        context: !editing,
        // The inspector's Clear removes the widget; the view config's own
        // Delete would be redundant (and confusing next to it).
        configParameters: { delete: false },
      };
    });

    const queryParams: GraphVariableDictionary = {
      self_id: {
        type: "string",
        value: computed(() => rowId.value ?? ""),
      },
    };

    const storagePrefix = computed(
      () =>
        `canvases/${props.organizationId}/${workspaceId.value}/${rowId.value}/widgets`
    );

    const canvasBody = shallowRef<CanvasBodyV1 | null>(null);
    const activeTabId = ref<string | null>(null);
    // Selection is by slot path (not widget id) so empty slots are
    // selectable too: clicking any slot highlights it, and an empty
    // selection keeps the palette in the right pane to fill it.
    const selectedPath = ref<CanvasSlotPath | null>(null);
    const renamingTabId = ref<string | null>(null);
    const tabNameDraft = ref("");
    const tabRenameInputEl = ref<HTMLInputElement | null>(null);
    // Function ref: a string ref inside the tabs `v-for` would collect an
    // array, breaking the focus() in startRenamingActiveTab. Only one rename
    // input ever exists (`v-if` on the renamed tab).
    const setTabRenameInputEl = (el: unknown) => {
      if (el instanceof HTMLInputElement) tabRenameInputEl.value = el;
      else if (!el) tabRenameInputEl.value = null;
    };

    const selectedDate = ref(new Date());
    const emptyCalendarsMap = ref<Record<string, unknown>>({});

    const widgetDate = computed(() => {
      if (canvasBody.value?.options?.datepicker) {
        return selectedDate.value;
      }
      return new Date();
    });

    const addDay = (n: number) => {
      const d = new Date(selectedDate.value);
      d.setDate(d.getDate() + n);
      selectedDate.value = d;
    };

    const goToday = () => {
      selectedDate.value = new Date();
    };

    const selectDate = (date: Date) => {
      selectedDate.value = date;
    };

    const ensureCanvasBody = () => {
      if (canvasBody.value) return;
      const parsed = parseCanvasBodyV1(bodyJson.value);
      canvasBody.value = parsed ?? defaultCanvasBodyV1();
      activeTabId.value = canvasBody.value.tabs[0]?.id ?? null;
    };

    releaser.defer(
      watch(
        [isLoading, error, bodyJson],
        ([loading, err]) => {
          if (loading) return;
          if (err) return;
          ensureCanvasBody();
        },
        { immediate: true }
      )
    );

    const currentTab = computed<CanvasTab | null>(() => {
      const body = canvasBody.value;
      if (!body) return null;
      const id = activeTabId.value ?? body.tabs[0]?.id ?? null;
      if (!id) return null;
      return body.tabs.find((t) => t.id === id) ?? null;
    });

    const currentRoot = computed<CanvasSlot>(
      () => currentTab.value?.root ?? null
    );

    // The tab's vertical behavior (see `CanvasTabHeight`): "screen" pins
    // the canvas to the viewport (widgets scroll internally), "page" flows
    // and scrolls like a document. Absent means "screen".
    const heightMode = computed<CanvasTabHeight>(
      () => currentTab.value?.height ?? "screen"
    );

    // The selected widget drives the right pane: palette when nothing is
    // selected (or when the selected slot is empty — the palette is how it
    // gets filled), the widget's inspector otherwise. The selected view
    // widget teleports its configuration into `viewConfigHostEl` (see
    // `View.configHost`). A stale path (e.g. its subtree was removed)
    // resolves to nothing and the selection self-heals.
    const selectedWidget = computed<CanvasWidget | undefined>(() => {
      const path = selectedPath.value;
      if (!path) return undefined;
      return getSlotAt(currentRoot.value, path) ?? undefined;
    });
    const selectedWidgetTitle = computed(() => {
      const widget = selectedWidget.value?.widget;
      if (!widget) return "";
      if (widget.kind === "view") return "View";
      if (widget.kind === "timeline") return "Timeline";
      if (widget.kind === "tasks") return "Tasks";
      if (widget.kind === "calendar") return "Calendar";
      return `Layout ${widget.rows}×${widget.columns}`;
    });
    const viewConfigHostEl = ref<HTMLElement | null>(null);

    const updateCurrentTab = (updater: (tab: CanvasTab) => CanvasTab) => {
      const body = canvasBody.value;
      const tab = currentTab.value;
      if (!body || !tab) return;
      const nextTabs = body.tabs.map((t) => (t.id === tab.id ? updater(t) : t));
      canvasBody.value = withTabs(body, nextTabs);
    };

    const updateCurrentRoot = (updater: (root: CanvasSlot) => CanvasSlot) => {
      updateCurrentTab((tab) => ({ ...tab, root: updater(tab.root) }));
    };

    //
    // Widget mutations, called back from the recursive slot nodes.
    //

    const onPaletteDragStart = (
      e: DragEvent,
      payload: CanvasWidgetDropPayload
    ) => {
      if (!e.dataTransfer) return;
      writeWidgetDropPayload(e.dataTransfer, payload);
    };

    const dropWidget = (
      path: CanvasSlotPath,
      payload: CanvasWidgetDropPayload
    ) => {
      if (!isEditMode.value) return;
      ensureCanvasBody();
      const widget = widgetFromPayload(payload);
      let placed = false;
      updateCurrentRoot((root) => {
        // Only empty slots accept drops (the node enforces it too).
        if (getSlotAt(root, path) !== null) return root;
        placed = true;
        return setSlotAt(root, path, widget);
      });
      if (placed) selectedPath.value = [...path];
    };

    // The slot keeps its selection when its widget is removed: it is now an
    // empty, highlighted slot with the palette shown — ready to be refilled.
    const removeWidget = (id: string) => {
      updateCurrentRoot((root) => removeWidgetById(root, id));
    };

    // Enable/disable one section of a Tasks widget; the enabled set is
    // stored in canonical order (see `CANVAS_TASKS_SECTIONS`).
    const toggleTasksSection = (id: string, section: CanvasTasksSection) => {
      updateCurrentRoot((root) =>
        updateWidgetById(root, id, (widget) => {
          const body = widget.widget;
          if (body.kind !== "tasks") return widget;
          const sections = body.sections.includes(section)
            ? body.sections.filter((entry) => entry !== section)
            : CANVAS_TASKS_SECTIONS.filter(
                (entry) => entry === section || body.sections.includes(entry)
              );
          return { ...widget, widget: { kind: "tasks", sections } };
        })
      );
    };

    // Flip one of a Calendar widget's show/hide options; a field is only
    // ever `false` or absent (absent means shown, the default).
    const toggleCalendarOption = (id: string, option: CanvasCalendarToggle) => {
      updateCurrentRoot((root) =>
        updateWidgetById(root, id, (widget) => {
          const body = widget.widget;
          if (body.kind !== "calendar") return widget;
          return {
            ...widget,
            widget: { ...body, [option]: body[option] === false },
          };
        })
      );
    };

    // Set a Calendar widget's default layout (from the inspector selector).
    const setCalendarLayout = (id: string, value: string) => {
      const layout = CANVAS_CALENDAR_LAYOUTS.find((entry) => entry === value);
      if (!layout) return;
      updateCurrentRoot((root) =>
        updateWidgetById(root, id, (widget) => {
          const body = widget.widget;
          if (body.kind !== "calendar") return widget;
          return { ...widget, widget: { ...body, layout } };
        })
      );
    };

    // Backspace/Delete clears the selected slot's widget while editing —
    // same as the inspector's Clear button. `assignShortcuts` already keeps
    // the keys out of focused inputs (title, tab rename, view config).
    const editShortcuts = computed<Shortcuts>(() => {
      if (!isEditMode.value) return {};
      return assignShortcuts(
        { isModalShown, isInputFocused },
        clearSelectedSlotKeys,
        () => {
          const widget = selectedWidget.value;
          // No widget under the selection: let the key propagate.
          if (!widget) return false;
          removeWidget(widget.id);
        }
      );
    });

    const selectSlot = (path: CanvasSlotPath | null) => {
      selectedPath.value = path ? [...path] : null;
    };

    // An empty slot is selected: the palette doubles as its inspector.
    const isEmptySlotSelected = computed(
      () => selectedPath.value !== null && !selectedWidget.value
    );

    // Deleting a selected empty slot removes its track from the parent
    // layout: the column when the parent is a single row (a 1×3 row's
    // middle slot deletes to a 1×2), the row when it is a single column. A
    // lone cell cannot leave a two-dimensional grid, so there the pane
    // offers both "Remove row" and "Remove column". Widgets elsewhere in
    // the removed track relocate to free cells; an action that would lose
    // one is disabled (see `removeCanvasLayoutTrack`).
    const emptySlotActions = computed<EmptySlotAction[]>(() => {
      const path = selectedPath.value;
      // Root slot: not inside any layout, nothing to delete.
      if (!path || path.length === 0) return [];
      if (selectedWidget.value) return []; // Filled: the inspector handles it.
      const parent = getSlotAt(currentRoot.value, path.slice(0, -1));
      if (!parent || parent.widget.kind !== "layout") return [];
      const layout = parent.widget;
      const childIndex = path[path.length - 1] ?? 0;
      const row = Math.floor(childIndex / layout.columns);
      const column = childIndex % layout.columns;

      const action = (
        label: string,
        axis: "rows" | "columns",
        index: number
      ): EmptySlotAction => ({
        label,
        parentId: parent.id,
        axis,
        index,
        enabled: removeCanvasLayoutTrack(layout, axis, index) !== null,
      });
      if (layout.rows === 1 && layout.columns === 1) return [];
      if (layout.rows === 1) return [action("Remove", "columns", column)];
      if (layout.columns === 1) return [action("Remove", "rows", row)];
      return [
        action("Remove row", "rows", row),
        action("Remove column", "columns", column),
      ];
    });

    const deleteEmptySlotTrack = (action: EmptySlotAction) => {
      if (!action.enabled) return;
      updateCurrentRoot((root) =>
        updateWidgetById(root, action.parentId, (w) => {
          if (w.widget.kind !== "layout") return w;
          const widget = removeCanvasLayoutTrack(
            w.widget,
            action.axis,
            action.index
          );
          return widget ? { ...w, widget } : w;
        })
      );
      // The deleted slot's path now points at another cell (or out of
      // range): drop the selection instead of highlighting a bystander.
      selectedPath.value = null;
    };

    // Persist a separator drag: the axis' track sizes as normalized
    // fractions (see `resizeTrackPair`) — `fr` units render them as
    // percentages of the axis, so a 2×2 defaults to 50/50 and dragging
    // yields e.g. 30/70 regardless of the screen size.
    const resizeLayoutTracks = (
      id: string,
      axis: "rows" | "columns",
      sizes: number[]
    ) => {
      updateCurrentRoot((root) =>
        updateWidgetById(root, id, (w) =>
          w.widget.kind !== "layout"
            ? w
            : {
                ...w,
                widget:
                  axis === "rows"
                    ? { ...w.widget, rowSizes: sizes }
                    : { ...w.widget, columnSizes: sizes },
              }
        )
      );
    };

    const setWidgetQuery = (id: string, query: Query) => {
      updateCurrentRoot((root) =>
        updateWidgetById(root, id, (w) =>
          w.widget.kind === "view"
            ? { ...w, widget: { ...w.widget, query: query.current } }
            : w
        )
      );
    };

    // Reshape the selected layout widget by one row/column step, preserving
    // its content (see `resizeCanvasLayout`). The paired predicate drives
    // the steppers' disabled state: out of bounds, or a shrink that would
    // lose a widget.
    const resizedSelectedLayout = (
      rowsDelta: number,
      columnsDelta: number
    ): { id: string; layout: CanvasLayoutWidget } | null => {
      const selected = selectedWidget.value;
      if (!selected || selected.widget.kind !== "layout") return null;
      const rows = selected.widget.rows + rowsDelta;
      const columns = selected.widget.columns + columnsDelta;
      if (rows > MAX_LAYOUT_TRACKS || columns > MAX_LAYOUT_TRACKS) return null;
      const layout = resizeCanvasLayout(selected.widget, rows, columns);
      return layout ? { id: selected.id, layout } : null;
    };

    const canResizeSelectedLayout = (
      rowsDelta: number,
      columnsDelta: number
    ): boolean => resizedSelectedLayout(rowsDelta, columnsDelta) !== null;

    const resizeSelectedLayout = (rowsDelta: number, columnsDelta: number) => {
      const resized = resizedSelectedLayout(rowsDelta, columnsDelta);
      if (!resized) return;
      updateCurrentRoot((root) =>
        updateWidgetById(root, resized.id, (w) => ({
          ...w,
          widget: resized.layout,
        }))
      );
    };

    // Store the display document the view emits (see `View.display`); its
    // schema is the views module's — the canvas only checks it is plain JSON.
    const setWidgetDisplay = (id: string, display: unknown) => {
      if (!isCanvasJsonValue(display)) return;
      updateCurrentRoot((root) =>
        updateWidgetById(root, id, (w) =>
          w.widget.kind === "view"
            ? { ...w, widget: { ...w.widget, display } }
            : w
        )
      );
    };

    //
    // Persistence.
    //

    const persistCanvasBody = async () => {
      const body = canvasBody.value;
      if (!body) return;
      const wsId = workspaceId.value;
      const dbId = tableId.value;
      const targetId = rowId.value;
      if (!wsId || !dbId || !targetId) return;

      const db =
        session.db.value ??
        (await controller.graph.database.get(wsId, dbId)).release();
      if (!db) return;

      setJsonProperty(body, "Body", targetId, db, controller, undefined);
    };

    const savedJson = ref<string | null>(null);

    const isTitleDirty = computed(() => draftTitle.value !== savedTitle.value);
    const isDirty = computed(() => {
      if (isTitleDirty.value) return true;
      const body = canvasBody.value;
      if (!body) return false;
      return JSON.stringify(body) !== savedJson.value;
    });

    const toggleEditMode = () => {
      if (!isEditMode.value) {
        ensureCanvasBody();
        savedJson.value = JSON.stringify(canvasBody.value);
        draftTitle.value = canvasTitle.value;
        savedTitle.value = canvasTitle.value;
      }
      isEditMode.value = !isEditMode.value;
      if (!isEditMode.value) selectedPath.value = null;
      if (!isEditMode.value) renamingTabId.value = null;
    };

    // `startInEditMode` (a canvas freshly created from the Studio screen):
    // enter edit mode once the canvas is loaded, going through
    // `toggleEditMode` so the dirty-tracking baselines are set. One-shot:
    // leaving edit mode afterwards must not re-enter it.
    let startEditPending = props.startInEditMode;
    releaser.defer(
      watch(
        [isLoading, error],
        ([loading, err]) => {
          if (!startEditPending || loading || err) return;
          startEditPending = false;
          if (!isEditMode.value) toggleEditMode();
        },
        { immediate: true }
      )
    );

    const persistTitle = () => {
      const newName = draftTitle.value.trim();
      if (!newName || newName === savedTitle.value) return;
      const db = session.db.value;
      const id = rowId.value;
      if (!db || !id) return;
      // setGraphPropertyRaw interpolates the value into a graph query,
      // so it must be a quoted string with inner backslashes and quotes escaped.
      const escapedValue = `"${newName.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
      setGraphPropertyRaw(
        escapedValue,
        session.titleFieldName.value,
        id,
        db,
        controller,
        undefined
      );
      savedTitle.value = newName;
    };

    const saveAndExitEditMode = async () => {
      persistTitle();
      await persistCanvasBody();
      savedJson.value = JSON.stringify(canvasBody.value);
      isEditMode.value = false;
      selectedPath.value = null;
      renamingTabId.value = null;
    };

    const cancelEditMode = () => {
      if (savedJson.value) {
        const restored = parseCanvasBodyV1(JSON.parse(savedJson.value));
        if (restored) {
          canvasBody.value = restored;
          activeTabId.value = restored.tabs[0]?.id ?? null;
        }
      }
      draftTitle.value = savedTitle.value;
      isEditMode.value = false;
      selectedPath.value = null;
      renamingTabId.value = null;
    };

    //
    // Tabs.
    //

    const selectTab = (id: string) => {
      commitTabRename();
      activeTabId.value = id;
      selectedPath.value = null;
    };

    const startRenamingActiveTab = async () => {
      if (!isEditMode.value) return;
      const tab = currentTab.value;
      if (!tab) return;
      renamingTabId.value = tab.id;
      tabNameDraft.value = tab.name;
      await nextTick();
      tabRenameInputEl.value?.focus();
      tabRenameInputEl.value?.select();
    };

    const cancelTabRename = () => {
      renamingTabId.value = null;
      tabNameDraft.value = "";
    };

    const commitTabRename = () => {
      const body = canvasBody.value;
      const id = renamingTabId.value;
      if (!body || !id) return;

      const nextName = tabNameDraft.value.trim();
      const tabs = body.tabs.map((t) =>
        t.id === id ? { ...t, name: nextName || t.name } : t
      );
      canvasBody.value = withTabs(body, tabs);
      renamingTabId.value = null;
      tabNameDraft.value = "";
    };

    const addTab = () => {
      ensureCanvasBody();
      const body = canvasBody.value;
      if (!body) return;
      const newTab: CanvasTab = {
        id: makeId(),
        name: `Tab ${body.tabs.length + 1}`,
        root: null,
      };
      canvasBody.value = withTabs(body, [...body.tabs, newTab]);
      activeTabId.value = newTab.id;
      selectedPath.value = null;
      renamingTabId.value = null;
    };

    const canDeleteActiveTab = computed(
      () => (canvasBody.value?.tabs.length ?? 0) > 1
    );

    /** Delete the active tab (with its whole slot tree) and activate its
     * right neighbor (or the new last tab). The last tab is not deletable. */
    const deleteActiveTab = () => {
      const body = canvasBody.value;
      if (!body || body.tabs.length <= 1) return;
      const id = activeTabId.value ?? body.tabs[0]?.id;
      const index = body.tabs.findIndex((t) => t.id === id);
      if (index < 0) return;
      const tabs = body.tabs.filter((t) => t.id !== id);
      canvasBody.value = withTabs(body, tabs);
      activeTabId.value = (tabs[index] ?? tabs[tabs.length - 1])?.id ?? null;
      selectedPath.value = null;
      renamingTabId.value = null;
    };

    //
    // Canvas-level options (the pane's "Canvas" section). Each option is a
    // toggle with its own default (see `CanvasOptions`).
    //

    const titleEnabled = computed(
      () => canvasBody.value?.options?.title !== false
    );

    const topBarDatepickerEnabled = computed(
      () => !!canvasBody.value?.options?.datepicker
    );

    const toggleOption = (option: "title" | "datepicker") => {
      ensureCanvasBody();
      const body = canvasBody.value;
      if (!body) return;
      const current =
        option === "title"
          ? body.options?.title !== false
          : !!body.options?.datepicker;
      canvasBody.value = {
        ...body,
        options: { ...body.options, [option]: !current },
      };
    };

    releaser.defer(
      watch(
        [objectData, record],
        ([raw, rec]) => {
          if (raw === undefined) return;
          if (raw === null) {
            objectError.value = "Canvas not found.";
            return;
          }
          if (!rec) {
            objectError.value = "Canvas data is not readable.";
            return;
          }
        },
        { immediate: true }
      )
    );

    return {
      database: session.db,
      hierarchy: session.hierarchy,
      isLoading,
      title,
      bodyValue,
      bodyJson,
      canvasTitle,
      profile,
      getTypeColor,
      getTypeIcon,
      openDatabase,
      draftTitle,
      error,
      canvasBody,
      isDirty,
      isEditMode,
      toggleEditMode,
      saveAndExitEditMode,
      cancelEditMode,
      activeTabId,
      selectTab,
      addTab,
      canDeleteActiveTab,
      deleteActiveTab,
      renamingTabId,
      tabNameDraft,
      setTabRenameInputEl,
      startRenamingActiveTab,
      cancelTabRename,
      commitTabRename,
      queryParams,
      viewParameters,
      storagePrefix,
      currentRoot,
      heightMode,
      layoutPalette: LAYOUT_PALETTE,
      onPaletteDragStart,
      dropWidget,
      removeWidget,
      toggleTasksSection,
      tasksSectionOptions: TASKS_SECTION_OPTIONS,
      toggleCalendarOption,
      setCalendarLayout,
      calendarToggleOptions: CALENDAR_TOGGLE_OPTIONS,
      calendarLayoutOptions: CALENDAR_LAYOUT_OPTIONS,
      editShortcuts,
      selectSlot,
      setWidgetQuery,
      setWidgetDisplay,
      resizeLayoutTracks,
      isEmptySlotSelected,
      emptySlotActions,
      deleteEmptySlotTrack,
      canResizeSelectedLayout,
      resizeSelectedLayout,
      selectedPath,
      selectedWidget,
      selectedWidgetTitle,
      viewConfigHostEl,
      titleEnabled,
      topBarDatepickerEnabled,
      toggleOption,
      selectedDate,
      widgetDate,
      addDay,
      goToday,
      selectDate,
      emptyCalendarsMap,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/context-actions.scss";

.canvas-page {
  width: 100%;
  height: 100%;
  position: relative;
}

.save-floating {
  position: fixed;
  bottom: 24px;
  inset-inline-end: 24px;
  z-index: 10;
  display: flex;
  flex-direction: row;
  gap: 8px;
}

.canvas-screen {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  // Slim padding: canvases build whole screens, so the content gets nearly
  // the full viewport (unlike an object page's wide 64px gutters).
  padding: 16px;
  box-sizing: border-box;

  // Screen height mode: the canvas owns exactly the viewport. The whole
  // chain down to the root slot becomes flexible with `min-height: 0` so
  // the layout grid divides the available height (fr row sizes) instead of
  // growing with content — widgets scroll internally, the page never does.
  &.height-screen {
    overflow: hidden;

    .canvas-content {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .canvas-body {
      flex: 1;
      min-height: 0;
    }

    .canvas-main {
      min-height: 0;
    }

    .workspace {
      min-height: 0;
    }

    // The pane keeps its own scrollbar: its content (palette, hosted view
    // config) can be taller than the viewport.
    .canvas-right-pane {
      min-height: 0;
      overflow-y: auto;
    }
  }
}

.canvas-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  column-gap: 16px;
  padding: 0 0 24px 0;
}

.canvas-heading {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  // Same chip-to-title rhythm as an object page (`.object-content`'s
  // row-gap between the type chip and the title row).
  row-gap: 4px;
  flex: 1;

  // Compensate the chip's own 6px horizontal padding so its icon
  // left-aligns with the title, as on object pages.
  :deep(.base-type) {
    margin-inline-start: -6px;
  }
}

.canvas-title {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  // Drop the UA's default h1 margins; the heading's row-gap and the
  // header's bottom padding own the spacing.
  margin: 0;

  .name {
    @extend %font-h2;
  }

  .title-input {
    flex: 1;

    :deep(.input) {
      @extend %font-h2;
    }
  }
}

.canvas-content {
  width: 100%;
}

.canvas-body {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 520px;
  column-gap: 24px;
}

.canvas-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
}

.tabs {
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 8px;

  button.base-button.none.auxiliary.default.selected,
  button.base-button.none.auxiliary.default:active {
    @include themed() {
      background-color: t($grey-05);
      color: t($primary);
    }
  }

  button.base-button.none.auxiliary.default {
    @include themed() {
      color: t($grey-50);
    }
  }
}

.tabs-spacer {
  flex: 1;
}

.tab-name {
  display: inline-block;

  &.editable {
    &:hover {
      cursor: text;
    }
  }
}

.tab-rename-input {
  @extend %font-caption-medium;
  border: none;
  outline: none;
  background: transparent;
  width: 160px;
  padding: 0;
  margin: 0;

  @include themed() {
    color: t($primary);
  }
}

.tab-add {
  :deep(svg) {
    width: 14px;
    height: 14px;
  }
}

.workspace {
  flex: 1;
  display: flex;
  align-items: stretch;
  justify-content: stretch;

  > .root-slot {
    flex: 1;
    min-width: 0;
  }
}

.canvas-right-pane {
  width: 380px;
  flex: 0 0 380px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
}

.pane-section {
  border-radius: 12px;
  padding: 12px;
  box-sizing: border-box;
  @include themed() {
    background: t($grey-03);
    border: 1px solid t($grey-10);
  }
}

.pane-title {
  @extend %font-caption-medium;
  margin-bottom: 10px;
  @include themed() {
    color: t($grey-70);
  }
}

.pane-subtitle {
  @extend %font-small-medium;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  margin: 10px 0 6px;
  @include themed() {
    color: t($grey-50);
  }
}

// The "Canvas" section's rows breathe like the widget inspectors' toggles
// (same 8px rhythm as .layout-inspector & co below).
.canvas-options {
  display: flex;
  flex-direction: column;
  row-gap: 8px;
}

.toggle-option {
  display: flex;
  align-items: center;
  justify-content: space-between;

  label {
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-80);
    }
  }
}

.inspector-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .pane-title {
    margin-bottom: 0;
  }
}

.inspector-close {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  @include themed() {
    background: transparent;
    color: t($grey-60);

    &:hover {
      background: t($grey-10);
      color: t($grey-80);
    }
  }

  :deep(svg) {
    width: 12px;
    height: 12px;
  }
}

.view-config-host {
  margin-top: 10px;

  // The hosted ViewConfig sizes itself for a popover; fill the pane instead.
  :deep(.query-config) {
    width: 100%;
    max-width: 100%;
  }
}

.inspector-hint {
  margin: 10px 0 0;
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-60);
  }
}

.layout-inspector,
.tasks-inspector,
.calendar-inspector {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  row-gap: 8px;
}

.calendar-inspector .select-row {
  display: flex;
  flex-direction: column;
  row-gap: 4px;

  label {
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-80);
    }
  }

  .layout-select {
    width: 100%;
  }
}

.size-row {
  display: flex;
  align-items: center;
  justify-content: space-between;

  label {
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-80);
    }
  }
}

.stepper {
  display: flex;
  align-items: center;
  column-gap: 4px;

  .count {
    @extend %font-caption-medium;
    min-width: 20px;
    text-align: center;
    @include themed() {
      color: t($grey-80);
    }
  }

  button {
    width: 24px;
    height: 24px;
    border-radius: 8px;
    border: none;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    @extend %font-caption-medium;

    @include themed() {
      background: t($grey-10);
      color: t($grey-80);

      &:hover:not(:disabled) {
        background: t($grey-20);
      }

      &:disabled {
        color: t($grey-40);
        cursor: default;
      }
    }
  }
}

// The inspector's Clear action (same shape as the DatePicker's).
.clear-row {
  display: flex;
  margin-top: 12px;

  :deep(.base-button .left),
  :deep(.base-button .left .text) {
    width: 100%;
  }
}

.clear-button {
  flex: 1;
  justify-content: center;

  :deep(.text) {
    font-size: 12px;
  }
}

.widget-palette {
  display: flex;
  flex-direction: column;
  row-gap: 8px;

  &.layout-palette {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
  }
}

.palette-item {
  @extend %font-caption-medium;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: grab;
  user-select: none;
  @include themed() {
    background: t($grey-03);
    border: 1px solid t($grey-10);
    color: t($grey-80);

    &:hover {
      background: t($grey-10);
    }
  }

  &.layout-item {
    flex: 0 0 auto;
    min-width: 48px;
    text-align: center;
  }

  &:active {
    cursor: grabbing;
  }
}

.canvas-raw {
  width: 100%;
}

.canvas-loading,
.canvas-error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  width: 100%;

  p {
    margin: 0;
    @extend %font-body-regular;
    @include themed() {
      color: t($grey-60);
    }
  }
}
</style>
